import React, { createContext, useState, useContext, useEffect } from 'react';
import * as authService from '../services/api/authService';

// Create the authentication context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Function to get initial auth state from localStorage
const getInitialState = () => {
  try {
    const authToken = localStorage.getItem('authToken');
    const userJson = localStorage.getItem('userData');

    if (authToken && userJson) {
      // If we find the token and user, set initial state to "logged in"
      return {
        user: JSON.parse(userJson),
        token: authToken,
        isAuthenticated: true
      };
    }
  } catch (error) {
    console.error("Failed to parse auth data from localStorage", error);
    // Clear potentially corrupted data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
  }
  
  // Default state if nothing is found or an error occurs
  return {
    user: null,
    token: null,
    isAuthenticated: false
  };
};

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage
  const initialState = getInitialState();
  const [currentUser, setCurrentUser] = useState(initialState.user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem('authToken');
        
        if (token) {
          // Fetch current user data from the API to validate token and get fresh user data
          try {
            const userData = await authService.getCurrentUser();
            
            // Update localStorage with fresh user data
            localStorage.setItem('userData', JSON.stringify(userData));
            setCurrentUser(userData);
          } catch (e) {
            console.error('Failed to fetch user data:', e);
            // Clear token if it's invalid
            localStorage.removeItem('authToken');
            localStorage.removeItem('userData');
            setCurrentUser(null);
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // Clear token if it's invalid
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      
      // Use the authService to login
      const { access_token, user } = await authService.login(email, password);
      
      // Store token and set user in localStorage for persistence
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('userData', JSON.stringify(user));
      
      // Update state
      setCurrentUser(user);
      
      return user;
    } catch (error) {
      setError(error.message || 'فشل تسجيل الدخول');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async (callback) => {
    try {
      setLoading(true);
      
      // Clear local storage and state first to ensure UI updates immediately
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      setCurrentUser(null);
      
      // Call logout endpoint if available (non-critical)
      try {
        await authService.logout();
      } catch (logoutError) {
        // Just log the error but continue with client-side logout
        console.error('Backend logout endpoint error:', logoutError);
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLoading(false);
      // Execute callback if provided (e.g., for navigation)
      if (typeof callback === 'function') {
        callback();
      }
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      
      const result = await authService.register(userData);
      return result;
    } catch (error) {
      setError(error.message || 'فشل إنشاء الحساب');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Forgot password function
  const forgotPassword = async (email) => {
    try {
      setError(null);
      setLoading(true);
      
      const result = await authService.forgotPassword(email);
      return result;
    } catch (error) {
      setError(error.message || 'فشل إرسال رابط إعادة تعيين كلمة المرور');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Reset password with token
  const resetPassword = async (token, newPassword) => {
    try {
      setError(null);
      setLoading(true);
      
      const result = await authService.resetPassword({ 
        token, 
        password: newPassword 
      });
      return result;
    } catch (error) {
      setError(error.message || 'Password reset failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return currentUser?.role === role;
  };

  // Context value
  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    register,
    forgotPassword,
    resetPassword,
    hasRole,
    isAdmin: currentUser?.role === 'admin',
    isCommittee: currentUser?.role === 'committee',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
